import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  Text,
  Button,
  Image,
  IconButton,
  useDisclosure,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerHeader,
  DrawerBody,
} from "@chakra-ui/react";
import { useNavigate, useLocation } from "react-router-dom";
import { getPortalEntryPath } from "../api/client";

interface NavItem {
  label: string;
  href?: string;
  isButton?: boolean;
}

const navigation: NavItem[] = [
  {
    label: "Home",
    href: "/",
  },
  {
    label: "About",
    href: "/about",
  },
  {
    label: "Fund",
    href: "/fund",
  },
  {
    label: "Advisory",
    href: "/advisory",
  },
  {
    label: "Contact",
    href: "/contact",
  },
  {
    label: "Research & Insights",
    href: "/member/login",
  },
];


export const GreshamHeader: React.FC = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const navigate = useNavigate();
  const location = useLocation();

  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setScrollY(currentScrollY);
    };

    // Initialize scroll position
    setScrollY(window.scrollY);

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Reset scroll state when route changes
  useEffect(() => {
    // Force reset to transparent state
    setScrollY(0);
    // Also reset actual scroll position
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Smooth transition based on scroll position
  const maxScroll = 100; // Maximum scroll distance for full transition
  const scrollProgress = Math.min(scrollY / maxScroll, 1);
  const bgOpacity = scrollProgress * 0.5; // Start completely transparent, fade to subtle opacity when scrolled

  const getNavigationHref = (item: NavItem) => {
    if (item.label === "Research & Insights") {
      return getPortalEntryPath();
    }

    return item.href!;
  };

  // Consistent behavior on both mobile and desktop
  const bgColor = scrollProgress > 0.05 ? `rgba(255, 255, 255, ${bgOpacity})` : "transparent";
  const textColor = scrollProgress > 0.3 ? "black" : "white";
  const logoOpacity = 1; // Keep logo fully visible
  const backdropBlur = scrollProgress > 0.1 ? "blur(10px)" : "none";


  const handleNavigation = (href: string) => {
    navigate(href);
    window.scrollTo(0, 0);
    // Reset scroll state immediately
    setScrollY(0);
    onClose();
  };

  return (
    <Box
      as="header"
      className="has_ae_slider elementor-element elementor-element-860bbf9 e-con-full e-flex ae-bg-gallery-type-default e-con e-parent e-lazyloaded elementor-sticky elementor-sticky--active elementor-section--handles-inside"
      position="fixed"
      top={0}
      left={0}
      right={0}
      w="full"
      bg={bgColor}
      backdropFilter={backdropBlur}
      borderBottom={`1px solid ${scrollProgress > 0.3 ? "rgba(0, 0, 0, 0.1)" : "rgba(255, 255, 255, 0.2)"}`}
      zIndex={1000}
      h={{ base: "80px", md: "100px" }}
      display="flex"
      transition="background-color 0.3s ease, backdrop-filter 0.3s ease"
      data-id="860bbf9"
      data-element_type="container"
      data-settings='{"background_background":"classic","sticky":"top","sticky_effects_offset":90,"_ha_eqh_enable":false,"sticky_on":["desktop","tablet","mobile"],"sticky_offset":0,"sticky_anchor_link_offset":0}'
      css={{
        /* Mobile devices */
        "@media (max-width: 768px)": {
          height: "80px !important",
          zIndex: "9999 !important"
        },
        /* iPad Portrait */
        "@media (max-width: 1024px) and (orientation: portrait)": {
          height: "100px !important",
          zIndex: "1000 !important"
        },
        /* iPad Landscape */
        "@media (max-width: 1366px) and (orientation: landscape)": {
          height: "100px !important",
          zIndex: "1000 !important"
        },
        /* iPad Pro Portrait */
        "@media (max-width: 1080px) and (orientation: portrait)": {
          height: "100px !important",
          zIndex: "1000 !important"
        },
        /* iPad Pro Landscape */
        "@media (max-width: 1440px) and (orientation: landscape)": {
          height: "100px !important",
          zIndex: "1000 !important"
        }
      }}
    >
      {/* Left Container - Logo */}
      <Box
        className="has_ae_slider elementor-element elementor-element-a07d7a9 e-con-full e-flex ae-bg-gallery-type-default e-con e-child"
        data-id="a07d7a9"
        data-element_type="container"
        data-settings='{"_ha_eqh_enable":false}'
        w={{ base: "auto", md: "40%", lg: "25%" }}
        minW={{ base: "150px", sm: "180px", md: "280px" }}
        h={{ base: "80px", md: "100px" }}
        display="flex"
        alignItems="center"
        justifyContent={{ base: "flex-start", lg: "flex-end" }}
        px={{ base: 4, sm: 6, md: 8, lg: 12 }}
        css={{
          /* iPad specific adjustments */
          "@media (min-width: 768px) and (max-width: 1024px)": {
            minWidth: "320px !important",
            width: "45% !important",
            paddingLeft: "20px !important",
            paddingRight: "10px !important"
          },
          /* iPad Pro specific */
          "@media (min-width: 1024px) and (max-width: 1366px)": {
            minWidth: "350px !important",
            width: "40% !important",
            paddingLeft: "24px !important"
          }
        }}
      >
          <Box 
            className="elementor-element elementor-element-f2491fa logo-black elementor-widget elementor-widget-theme-site-logo elementor-widget-image"
            data-id="f2491fa"
            data-element_type="widget"
            data-widget_type="theme-site-logo.default"
            cursor="pointer"
            onClick={() => handleNavigation('/')}
          >
            <Box className="elementor-widget-container">
              <Image
                src={scrollProgress > 0.3 ? "/logo/logo_black.png" : "/logo/ME logo_Final_white.png"}
                alt="Mercurian Equity Home"
                className="attachment-full size-full wp-image-208"
                fetchPriority="high"
                h={{ base: "30px", sm: "35px", md: "50px", lg: "60px" }}
                w="auto"
                maxW={{ base: "140px", sm: "160px", md: "240px", lg: "260px" }}
                objectFit="contain"
                opacity={logoOpacity}
                transition="opacity 0.3s ease"
                css={{
                  /* iPad specific adjustments */
                  "@media (min-width: 768px) and (max-width: 1024px)": {
                    height: "55px !important",
                    maxWidth: "280px !important"
                  },
                  /* iPad Pro specific */
                  "@media (min-width: 1024px) and (max-width: 1366px)": {
                    height: "60px !important",
                    maxWidth: "300px !important"
                  }
                }}
              />
            </Box>
          </Box>
        </Box>

      {/* Right Container - Navigation Menu */}
      <Box
        className="has_ae_slider elementor-element elementor-element-a3da71e e-con-full e-flex ae-bg-gallery-type-default e-con e-child"
        data-id="a3da71e"
        data-element_type="container"
        data-settings='{"_ha_eqh_enable":false}'
        w={{ base: "auto", md: "60%", lg: "75%" }}
        flex={1}
        h={{ base: "80px", md: "100px" }}
        display="flex"
        alignItems="center"
        justifyContent={{ base: "flex-end", md: "flex-end", lg: "center" }}
        px={{ base: 4, sm: 6, md: 8, lg: 12 }}
        css={{
          /* iPad specific adjustments */
          "@media (min-width: 768px) and (max-width: 1024px)": {
            width: "55% !important",
            justifyContent: "flex-end !important",
            paddingRight: "20px !important"
          },
          /* iPad Pro specific */
          "@media (min-width: 1024px) and (max-width: 1366px)": {
            width: "60% !important",
            justifyContent: "center !important"
          }
        }}
      >
            {/* Mobile Menu Icon */}
            <Box
              className="elementor-element elementor-element-61f4566 elementor-hidden-desktop elementor-view-default elementor-widget elementor-widget-icon"
              data-id="61f4566"
              data-element_type="widget"
              data-widget_type="icon.default"
              display={{ base: "flex", md: "flex", lg: "none" }}
              alignItems="center"
              justifyContent="center"
            >
              <Box className="elementor-widget-container">
                <Box className="elementor-icon-wrapper">
                  <IconButton
                    className="elementor-icon"
                    onClick={onOpen}
                    icon={
                      <VStack spacing="2px">
                        <Box w="18px" h="2px" bg={textColor} />
                        <Box w="18px" h="2px" bg={textColor} />
                        <Box w="18px" h="2px" bg={textColor} />
                      </VStack>
                    }
                    variant="ghost"
                    aria-label="Open menu"
                    size="md"
                    _hover={{ bg: "transparent" }}
                  />
                </Box>
              </Box>
            </Box>
            
            {/* Desktop Navigation Widget */}
            <Box
              className="elementor-element elementor-element-9ff85c1 elementor-hidden-tablet elementor-hidden-mobile elementor-widget elementor-widget-ha-nav-menu happy-addon ha-nav-menu happy-addon-pro"
              data-id="9ff85c1"
              data-element_type="widget"
              data-widget_type="ha-nav-menu.default"
              display={{ base: "none", md: "none", lg: "block" }}
            >
              <Box className="elementor-widget-container">
                <Box
                  className="ha-wid-con ha-menu-nav-link-icon-position-left ha_menu_responsive_tablet"
                  data-hamburger-icon="fas fa-align-right"
                  data-hamburger-icon-type="icon"
                  data-responsive-breakpoint="1024"
                >
                  <Box
                    id="ha-megamenu-main-menu"
                    className="ha-menu-container ha-menu-offcanvas-elements ha-navbar-nav-default ha-nav-menu-one-page-no"
                  >
                    <Box
                      as="ul"
                      id="main-menu"
                      className="ha-navbar-nav ha-menu-po-center submenu-click-on-icon"
                      display="flex"
                      listStyleType="none"
                      alignItems="center"
                      justifyContent="center"
                      gap={{ base: 6, md: 8, lg: 10 }}
                      m={0}
                      p={0}
                      transform="translateY(25%)"
                      sx={{
                        "& > li": {
                          marginLeft: "0 !important",
                          marginRight: "0 !important",
                          paddingLeft: "0 !important",
                          paddingRight: "0 !important"
                        }
                      }}
                    >
                      {navigation.map((item, index) => (
                        (() => {
                          const href = getNavigationHref(item);

                          return (
                        <Box
                          as="li"
                          key={item.label}
                          id={`menu-item-${72 + index}`}
                          className={`menu-item menu-item-type-post_type menu-item-object-page menu-item-${item.label.toLowerCase()} menu-item-${72 + index} nav-item ha-mobile-builder-content`}
                          data-vertical-menu="750px"
                        >
                          <Box
                            as="a"
                            href={href}
                            className="ha-menu-nav-link"
                            cursor="pointer"
                            onClick={(e: React.MouseEvent) => {
                              e.preventDefault();
                              if (item.isButton) {
                                window.open(href, '_blank', 'noopener,noreferrer');
                              } else {
                                handleNavigation(href);
                              }
                            }}
                            _hover={{ opacity: 0.7 }}
                            transition="opacity 0.2s"
                            textDecoration="none"
                          >
                            {item.isButton ? (
                              <Button
                                size="sm"
                                variant="outline"
                                borderRadius="full"
                                px={6}
                                fontFamily="'Carmen Sans Bold', sans-serif"
                                fontWeight="bold"
                                letterSpacing="1.5px"
                                color={textColor}
                                borderColor={textColor}
                                bg="transparent"
                                _hover={{ bg: "rgba(255,255,255,0.12)" }}
                                _active={{ bg: "rgba(255,255,255,0.2)" }}
                              >
                                {item.label}
                              </Button>
                            ) : (
                              <Text
                                as="span"
                                className="menu-item-title"
                                fontSize={{ base: "md", md: "lg" }}
                                fontFamily="'Carmen Sans Bold', sans-serif"
                                fontWeight="bold"
                                letterSpacing="1.5px"
                                color={textColor}
                              >
                                {item.label}
                              </Text>
                            )}
                          </Box>
                        </Box>
                          );
                        })()
                      ))}
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>

      </Box>


      {/* Mobile Navigation Drawer */}
      <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="sm">
        <DrawerOverlay bg="blackAlpha.500" />
        <DrawerContent
          bg="rgba(255, 255, 255, 0.1)"
          backdropFilter="blur(20px)"
          border="1px solid rgba(255, 255, 255, 0.2)"
          maxW="400px"
        >
          <DrawerCloseButton
            size="lg"
            _hover={{ bg: "rgba(255, 255, 255, 0.1)" }}
            color="white"
          />
          <DrawerHeader 
            borderBottomWidth="0px"
            pb={8}
            pt={12}
            textAlign="center"
          >
            <Image
              src="/logo/ME logo_Final_white.png"
              alt="Mercurian Equity"
              h="40px"
              objectFit="contain"
              mx="auto"
            />
          </DrawerHeader>
          <DrawerBody px={8} py={0}>
            <VStack align="center" spacing={6} h="full">
              <VStack spacing={4} flex="1" justify="center" w="full">
                {navigation.map((item) => (
                  (() => {
                    const href = getNavigationHref(item);

                    return (
                  <Box key={item.label} w="full">
                    {item.isButton ? (
                      <Button
                        w="full"
                        onClick={() => {
                          window.open(href, '_blank', 'noopener,noreferrer');
                          onClose();
                        }}
                        size="lg"
                        variant="outline"
                        borderRadius="full"
                        fontFamily="'Carmen Sans Bold', sans-serif"
                        fontWeight="bold"
                        letterSpacing="1px"
                        color="white"
                        borderColor="white"
                        bg="transparent"
                        _hover={{ bg: "rgba(255,255,255,0.16)" }}
                        _active={{ bg: "rgba(255,255,255,0.24)" }}
                      >
                        {item.label}
                      </Button>
                    ) : (
                      <Text
                        as="button"
                        w="full"
                        onClick={() => handleNavigation(href)}
                        fontSize="xl"
                        fontFamily="'Carmen Sans Bold', sans-serif"
                        fontWeight="bold"
                        letterSpacing="1px"
                        color="white"
                        textAlign="center"
                        py={2}
                        _hover={{ opacity: 0.7 }}
                        transition="opacity 0.2s"
                        cursor="pointer"
                        bg="transparent"
                        border="none"
                        textTransform="capitalize"
                      >
                        {item.label.toLowerCase()}
                      </Text>
                    )}
                  </Box>
                    );
                  })()
                ))}
              </VStack>
              
              {/* LinkedIn Icon at Bottom */}
              <Box pb={8}>
                <IconButton
                  as="a"
                  href="https://linkedin.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  icon={<Box as="i" className="fab fa-linkedin-in" />}
                  variant="ghost"
                  aria-label="LinkedIn"
                  color="white"
                  fontSize="24px"
                  _hover={{ opacity: 0.7, bg: "rgba(255, 255, 255, 0.1)" }}
                />
              </Box>
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Box>
  );
};